import { IconButton, Icon, Alert, Tooltip } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { useState, useMemo, useEffect, useCallback } from "react";
import { DataTypeEnum } from "../../_common/enum/dataType.enum";
import { FormStatusEnum } from "../../_common/enum/forms/formStatus.enum";
import { ITableData, IFilteringViewModel } from "../../_common/interface/table";
import { SearchTableHeader } from "../../_components/table/search.table";
import { StandardTable } from "../../_components/table/standard.table";
import { TABLE_FILTER } from "../../_const/tableFilter.const";
import { getDateTime } from "../../_helpers/dateTime.helper";
import { handleErrorResponse } from "../../_helpers/handleResponse.helper";
import { formService } from "../../_services/form.service";
import { PaperTableHeader } from "../../_components/table/header.table";
import { accountService } from "../../_services/accounts.service";
import { toastr } from "../../_components/toastr/toastr";

function formStatusCell(rowData: any) {
    return (
        <>
            {rowData.formStatusId == FormStatusEnum.New ? (
                <Alert severity="info">{FormStatusEnum[rowData.formStatusId]}</Alert>
            ) : rowData.formStatusId == FormStatusEnum.InProcess ? (
                <Alert severity="info">In Process</Alert>    
            ) : rowData.formStatusId == FormStatusEnum.Approved ? (
                <Alert severity="success">{FormStatusEnum[rowData.formStatusId]}</Alert>
            ) : rowData.formStatusId == FormStatusEnum.AwaitingApproval ? (
                <Alert severity="warning">Awaiting Approval</Alert>
            ) : rowData.formStatusId == FormStatusEnum.Withdrawn ? (
                <Alert severity="warning">{FormStatusEnum[rowData.formStatusId]}</Alert>
            ) : rowData.formStatusId == FormStatusEnum.Cancelled ? (
                <Alert severity="error">{FormStatusEnum[rowData.formStatusId]}</Alert>
            ) : (
                <></>
            )}
        </>
    );
}

function canShowQuickApprove(statusId: number): boolean {
    return (
        statusId === FormStatusEnum.AwaitingApproval || statusId === FormStatusEnum.CompletedAwaitingApproval
    );
}

export function FormApprovalIndex() {
    const [data, setData] = useState<ITableData<any>>({ items: [], count: 0 });
    const [isLoading, setLoading] = useState(true);
    const [filter, setFilter] = useState<IFilteringViewModel>({
        ...TABLE_FILTER,
        formStatusId: FormStatusEnum.AwaitingApproval,
    });
    const [canApproveFromTable, setCanApproveFromTable] = useState(false);
    const [approvingId, setApprovingId] = useState<number | null>(null);

    useEffect(() => {
        const sync = () => setCanApproveFromTable(accountService.isAdmin || accountService.isManager);
        sync();
        const sub = accountService.currentUser.subscribe(sync);
        return () => sub.unsubscribe();
    }, []);

    const fetchData = useCallback(() => {
        setLoading(true);
        return formService
            .getByCentre({ ...filter })
            .then(
                (d) => {
                    setData(d);
                },
                handleErrorResponse
            )
            .finally(() => {
                setLoading(false);
            });
    }, [filter]);

    useEffect(() => {
        if (filter) fetchData();
    }, [filter, fetchData]);

    const handleApprove = useCallback(
        (id: number) => {
            setApprovingId(id);
            return formService
                .approve(id)
                .then(() => {
                    toastr.success("Form approved");
                    return fetchData();
                }, handleErrorResponse)
                .finally(() => setApprovingId(null));
        },
        [fetchData]
    );

    const columns = useMemo<MRT_ColumnDef<any>[]>(
        () => [
            {
                id: "actions",
                header: "Actions",
                size: 80,
                enableSorting: false,
                enableColumnFilter: false,
                Cell: ({ row }) => (
                    <>
                        <IconButton
                            color="primary"
                            onClick={() => window.open(`Forms/${row.original.id}`, "_blank")}
                        >
                            <Icon>edit</Icon>
                        </IconButton>
                    </>
                ),
            },
            ...(canApproveFromTable
                ? [
                      {
                          id: "approve",
                          header: "Approve",
                          size: 72,
                          enableSorting: false,
                          enableColumnFilter: false,
                          Cell: ({ row }: { row: any }) => {
                              const id = row.original.id as number;
                              const statusId = row.original.formStatusId as number;
                              const show = canShowQuickApprove(statusId);
                              if (!show) return null;
                              const busy = approvingId === id;
                              return (
                                  <Tooltip title="Approve form">
                                      <span>
                                          <IconButton
                                              color="success"
                                              size="small"
                                              disabled={busy}
                                              onClick={() => handleApprove(id)}
                                              aria-label="Approve form"
                                          >
                                              <Icon>thumb_up_alt</Icon>
                                          </IconButton>
                                      </span>
                                  </Tooltip>
                              );
                          },
                      } as MRT_ColumnDef<any>,
                  ]
                : []),
            {
                accessorKey: "name",
                header: "Name",
                minSize: 180,
            },
            {
                accessorKey: "centreName",
                header: "Centre",
                minSize: 180,
            },
            {
                accessorKey: "formStatusId",
                header: "Form Status",
                accessorFn: (rowData: any) => <>{formStatusCell(rowData)}</>,
            },
            {
                accessorKey: "createdDate",
                header: "Created Date",
                accessorFn: (rowData: any) => <>{rowData.createdDate ? getDateTime(rowData.createdDate) : "-"}</>,
            },
            {
                accessorKey: "modifiedDate",
                header: "Modified Date",
                accessorFn: (rowData: any) => <>{rowData.modifiedDate ? getDateTime(rowData.modifiedDate) : "-"}</>,
            },
            {
                accessorKey: "modifiedBy",
                header: "Modified By",
            },
            {
                accessorKey: "topic",
                header: "Topic",
                minSize: 180,
            },
            {
                accessorKey: "referenceNumber",
                header: "Reference",
            },
        ],
        [canApproveFromTable, approvingId, handleApprove]
    );

    return (
        <>
            <PaperTableHeader title={"Form Approval"} />
            <SearchTableHeader
                inputs={[
                    {
                        label: "Centre",
                        name: "centreId",
                        type: "autocomplete",
                        dataType: DataTypeEnum.CentreByUser,
                        onChange: setFilter,
                        value: filter.centreId,
                        width: 250,
                    },
                    {
                        label: "Form Type",
                        name: "formTemplateId",
                        type: "autocomplete",
                        dataType: DataTypeEnum.FormTemplate,
                        onChange: setFilter,
                        value: filter.formTemplateId,
                        width: 250,
                    },
                    { label: "Date From", name: "fromDate", type: "date", onChange: setFilter, value: filter.fromDate },
                    { label: "Date To", name: "toDate", type: "date", onChange: setFilter, value: filter.toDate },
                    {
                        label: "Status",
                        name: "formStatusId",
                        dataType: DataTypeEnum.FormStatus,
                        onChange: setFilter,
                        value: filter.formStatusId,
                        width: 250,
                    },
                ]}
            />

            <StandardTable
                columns={columns}
                data={data}
                filter={filter}
                setFilter={setFilter}
                loading={isLoading}
                maxHeightOffset={200}
            />
        </>
    );
}
